import { useEffect } from 'react';
import _keyBy from 'lodash/keyBy';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _filter from 'lodash/filter';
import _groupBy from 'lodash/groupBy';
import _flatMap from 'lodash/flatMap';
import _first from 'lodash/first';
import _last from 'lodash/last';
import _some from 'lodash/some';
import _every from 'lodash/every';
import _split from 'lodash/split';
import {
  sumHeights,
  sumArea,
  getBaseRowColSpan,
  mapChoiceSegments,
  getViolatingSegments,
  filterByInclusiveChoices
} from './utils';
import {
  useConfiguratorStore,
  useCompatibilityStore,
} from '../../store';
import { useDesignStore } from '../../store/design/context';
import normalizeChoiceInfo from '../../utils/normalizeChoiceInfo';
import { getHeightMap } from '../Visualizer/utils/index';
import {
  HANDLE_BASE_CHOICE_ID,
  isHandleStarterChoiceId,
} from '../Visualizer/utils/handleRules';

const Compatibility = () => {
  const design = useDesignStore((state) => state.design);
  const configurator = useConfiguratorStore((state) => state.configurator);
  const { metadata } = useConfiguratorStore((state) => state.configurator);
  const {
    choicesInfo,
    updateDisabled,
    updateChoicesInfo,
    updateViolatingSegments,
    updateCompatibilitySegments,
    updateCompatibilityHeights,
  } = useCompatibilityStore((state) => state);

  const { products, matrix } = design;
  const mainProducts = _filter(products, (product) => product?.targetArea !== 'handle');
  const handleProducts = _filter(products, (product) => product?.targetArea === 'handle');
  const { choices } = configurator;

  useEffect(() => {
    if (!_isEmpty(choices)) {
      const normalized = normalizeChoiceInfo(choices);
      updateChoicesInfo(normalized);
    }
  }, [choices]);

  useEffect(() => {
    if (_isEmpty(mainProducts)) {
      updateDisabled(null);
      updateCompatibilitySegments({});
      updateViolatingSegments({});
      updateCompatibilityHeights({});
      return;
    }

    const filteredChoiceIds = filterByInclusiveChoices(mainProducts, choicesInfo, matrix, metadata);
    const heightsMap = getHeightMap(mainProducts, metadata);

    if (!_isEmpty(mainProducts) && choicesInfo) {

      // START: MAP, FLATTEN, GROUP SEGMENTS, GROUP SEGMENT HEIGHTS
      const mappedChoiceSegments = _filter(
        mapChoiceSegments(matrix, mainProducts, choicesInfo),
        (choiceSegment) => !_isEmpty(choiceSegment)
      );
      const flattenedProductSegments = _flatMap(mappedChoiceSegments);
      const groupedProductSegments = _groupBy(
        flattenedProductSegments,
        ({ segment: { parent: { child: { start, end } } } }) => `${start.toString()}:${end.toString()}`
      );
      updateCompatibilitySegments(groupedProductSegments);
      const groupedProductSegmentHeights = _keyBy(_map(groupedProductSegments, (group) => {
        const { segment: { parent: { child: { start, end, companion } } } } = _first(group);
        const groupName = `${start.toString()}:${end.toString()}`;
        const companionGroupName = companion
          ? companion?.groupName
          : groupName;
        const groupHeight = sumHeights(group);
        const companionHeight = companion
          ? sumHeights(groupedProductSegments[companion?.groupName])
          : groupHeight;
        const lastInGroupType = _last(group)?.productType;

        return {
          groupName,
          groupHeight,
          companionGroupName,
          companionHeight,
          heightsEqual: groupHeight === companionHeight,
          selfOnlyTopOfGroup: lastInGroupType === 'Self',
        };
      }), 'groupName');
      // END: MAP, FLATTEN, GROUP SEGMENTS, GROUP SEGMENT HEIGHTS

      updateCompatibilityHeights(groupedProductSegmentHeights);
      const violatingSegmentHeights = _filter(
        groupedProductSegmentHeights,
        (group) => !(group.heightsEqual)
      );
      const firstViolationGroup = _first(violatingSegmentHeights);
      const lastViolationGroup = _last(violatingSegmentHeights);
      const baseProduct = _first(_filter(
        mainProducts,
        (product) => choicesInfo?.[product?.choiceId]?.productType === 'Base'
      )) || _first(mainProducts);
      const [baseRowSpan, baseColSpan] = getBaseRowColSpan(choicesInfo[baseProduct?.choiceId], matrix);
      const isMultiStack = (baseRowSpan === matrix?.rows && baseColSpan === matrix?.columns);
      const topOnlyItems = _filter(mainProducts, (product) => {
        const [,, tubeSpan] = product.Origin;
        return tubeSpan === -1;
      });
      const topItemsInRear = _filter(
        topOnlyItems,
        (item) => _first(item.Origin) === 0
      );
      const topItemsInFront = _filter(
        topOnlyItems,
        (item) => _first(item.Origin) === 2
      );

      const filteredSelfOnlyProducts = _filter(mainProducts, (product) => {
        const [,, tubeSpan] = product.Origin;
        return tubeSpan === 0;
      });

      const topMostSelfOnlyItems = _map(
        _groupBy(filteredSelfOnlyProducts, 'Origin'),
        (group) => _last(group)
      );
      const selfOnlyTotalCoverage = sumArea(topMostSelfOnlyItems, choicesInfo);
      const topOnlyTotalCoverage = sumArea(topOnlyItems, choicesInfo);
      const baseTotalCoverage = baseRowSpan === 2 && baseColSpan === 2
        ? (baseRowSpan * baseColSpan) * 2
        : baseRowSpan * baseColSpan;

      // filter out base and narrows
      const wideOnlyMultiStackGroups = _filter(groupedProductSegmentHeights, (group) => {
        const { groupName } = group;
        const splitGroupName = _split(groupName, ':');
        const [startRow,, startCol] = _first(splitGroupName);
        const [endRow,, endCol] = _last(splitGroupName);
        const wideGroupByRows = ((Number(endRow) - Number(startRow)) === 1);
        const wideGroupByCols = ((Number(endCol) - Number(startCol)) === (matrix?.columns - 1));
        return wideGroupByRows && wideGroupByCols;
      });

      const fullSelfCover = selfOnlyTotalCoverage === baseTotalCoverage;
      const selfOnlyCoveringTop = _every(wideOnlyMultiStackGroups, 'selfOnlyTopOfGroup');
      const selfOnlyAtTop = _some(wideOnlyMultiStackGroups, 'selfOnlyTopOfGroup');
      const disableSelfCondition = () => {
        if (fullSelfCover && selfOnlyCoveringTop) {
          return true;
        }

        if (isMultiStack) {
          if (selfOnlyAtTop) {
            if (
              topOnlyTotalCoverage >= ((baseRowSpan * baseColSpan) / 2)
              && (topItemsInFront.length === 1 || topItemsInRear.length === 1)
            ) {
              return true;
            }

            if (topItemsInFront.length === 2 || topItemsInRear.length === 2) {
              if (
                Number(firstViolationGroup?.groupName[0]) === topItemsInFront[0]?.Origin[0]
                    || Number(lastViolationGroup?.groupName[0]) === topItemsInFront[0]?.Origin[0]
                    || Number(firstViolationGroup?.groupName[0]) === topItemsInRear[0]?.Origin[0]
                    || Number(lastViolationGroup?.groupName[0]) === topItemsInRear[0]?.Origin[0]
              ) {
                return true;
              }

              if (!firstViolationGroup) {
                return true;
              }

              return false;
            }
          }
        }
        return false;
      };

      const sizesToDisable = getViolatingSegments(
        violatingSegmentHeights.length,
        isMultiStack,
        baseRowSpan,
        baseColSpan,
        topOnlyTotalCoverage,
        selfOnlyAtTop,
        topItemsInRear,
        topItemsInFront,
        firstViolationGroup,
        lastViolationGroup
      );
      updateViolatingSegments(violatingSegmentHeights);
      const disabledSizes = _filter(choicesInfo, (choiceInfo) => {
        const [rowSpan, colSpan] = choiceInfo.span;
        const { maxRowSize, maxColSize } = sizesToDisable;
        return (Number(rowSpan) > Number(maxRowSize)) || (Number(colSpan) > Number(maxColSize));
      });
      if (topOnlyTotalCoverage === baseTotalCoverage) {
        const allIds = _map(choicesInfo, ({ id }) => id);
        updateDisabled(allIds);
        return;
      }
      const disabledSelfOnly = disableSelfCondition()
        ? _filter(choicesInfo, (choiceInfo) => choiceInfo.visualizerType !== 'wide')
        : [];
      const disabledBases = _filter(choicesInfo, (choiceInfo) => choiceInfo.productType === 'Base');
      // disable full if heights are not equal across all segments
      const areHeightsEqual = (heightsMap.FL === heightsMap.FR)
        && (heightsMap.RL === heightsMap.RR)
        && (heightsMap.FL === heightsMap.RL);
      const disabledFull = !areHeightsEqual
        ? _filter(choicesInfo, (choiceInfo) => choiceInfo.visualizerType === 'full')
        : [];
      let disabledIds = _map(
        [...disabledBases, ...disabledSizes, ...disabledSelfOnly, ...disabledFull],
        ({ id }) => id
      );

      // Apply inclusiveSkus filtering
      if (filteredChoiceIds) {
        disabledIds = [...new Set([...disabledIds, ...filteredChoiceIds])];
      }

      // Keep main-area compatibility checks, but do not block handle workflow.
      const isHandleBasePresent = _some(
        mainProducts,
        (product) => product?.choiceId === HANDLE_BASE_CHOICE_ID
      );
      const hasHandleStack = !_isEmpty(handleProducts);
      if (isHandleBasePresent && !hasHandleStack) {
        disabledIds = _filter(disabledIds, (id) => !isHandleStarterChoiceId(id));
      }

      if (isHandleBasePresent && hasHandleStack) {
        const handleEligibleChoiceIds = _map(
          _filter(
            choicesInfo,
            (choiceInfo) => choiceInfo?.visualizerType === 'wide' || choiceInfo?.visualizerType === 'narrow'
          ),
          ({ id }) => id
        );
        disabledIds = _filter(disabledIds, (id) => !handleEligibleChoiceIds.includes(id));
      }

      updateDisabled(disabledIds);
      return;
    }
    if (filteredChoiceIds) {
      updateDisabled([...new Set(filteredChoiceIds)]);
    } else {
      updateDisabled(null);
    }
    updateCompatibilitySegments({});
  }, [products, choicesInfo]);

  return null;

};

export default Compatibility;
